import { useState, useEffect } from 'react'
import { PricingItemsTable } from '@/components/pricing/pricing-items-table'
import { PageLoader } from '@/components/ui/page-loader'
import { AddItemDialog, STRIPING_CATEGORY_OPTIONS } from '@/components/pricing/add-item-dialog'
import { DeleteItemDialog } from '@/components/pricing/delete-item-dialog'
import { pricingService } from '@/services/pricing-service'
import { companyService } from '@/services/company-service'
import type { PricingItem } from '@/types'

interface PricingItemsPageProps {
  category: string
  title: string
  addLabel?: string
}

export function PricingItemsPage({ category, title, addLabel }: PricingItemsPageProps) {
  const [items, setItems] = useState<PricingItem[]>([])
  const [search, setSearch] = useState('')
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingItem, setEditingItem] = useState<PricingItem | undefined>()
  const [activeTab, setActiveTab] = useState('All')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [defaultFuelChargePercent, setDefaultFuelChargePercent] = useState(18)
  const [pendingDelete, setPendingDelete] = useState<PricingItem | undefined>()
  const [deleteBlocked, setDeleteBlocked] = useState<string | undefined>()
  const [deleting, setDeleting] = useState(false)

  // Fetch items from API when category changes
  useEffect(() => {
    let cancelled = false
    const fetchItems = async () => {
      try {
        setLoading(true)
        setError(null)
        const data = await pricingService.list(category)
        if (!cancelled) {
          setItems(data)
          setSearch('')
          setActiveTab('All')
          setDialogOpen(false)
          setEditingItem(undefined)
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load pricing items:', err)
          setError('Failed to load pricing items')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchItems()
    return () => { cancelled = true }
  }, [category])

  // Fetch company default fuel charge
  useEffect(() => {
    companyService.getSettings().then((settings) => {
      if (settings.defaultFuelChargePercent !== undefined && settings.defaultFuelChargePercent !== null) {
        setDefaultFuelChargePercent(settings.defaultFuelChargePercent)
      }
    }).catch(() => { /* keep default */ })
  }, [])

  // Build service tabs from data — striping uses stripingCategory, others use service
  // Equipment service field may be comma-separated (multi-select), so split and flatten
  const serviceField = category === 'striping' ? 'stripingCategory' : 'service'
  const dataServices = Array.from(
    new Set(
      items.flatMap((i) => {
        const val = i[serviceField]
        if (!val) return []
        return val.split(',').map(s => s.trim()).filter(Boolean)
      })
    )
  )
  // Striping tabs always show every known category (even with 0 items today) so
  // there's a way to add the first item — otherwise an empty category is invisible.
  const uniqueServices = category === 'striping'
    ? Array.from(new Set([...STRIPING_CATEGORY_OPTIONS, ...dataServices]))
    : dataServices
  const tabs = ['All', ...uniqueServices]

  const handleAddClick = () => {
    setEditingItem(undefined)
    setDialogOpen(true)
  }

  const handleEdit = (item: PricingItem) => {
    setEditingItem(item)
    setDialogOpen(true)
  }

  // Deleting is permanent and the server may refuse (formula-linked items, or
  // items referenced by estimate line items), so confirm first and only drop the
  // row once the server has actually deleted it. The previous optimistic-remove
  // + silent rollback made a refusal look like a row that vanished and returned.
  const handleDelete = (id: string) => {
    const item = items.find((i) => i.id === id)
    if (item) setPendingDelete(item)
  }

  const confirmDelete = async () => {
    if (!pendingDelete) return
    const { id } = pendingDelete
    setDeleting(true)
    try {
      await pricingService.remove(id)
      setItems((prev) => prev.filter((item) => item.id !== id))
      setPendingDelete(undefined)
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
      console.error('Failed to delete item:', err)
      setPendingDelete(undefined)
      setDeleteBlocked(msg || 'This item could not be deleted. Please try again.')
    } finally {
      setDeleting(false)
    }
  }

  const closeDeleteDialog = () => {
    setPendingDelete(undefined)
    setDeleteBlocked(undefined)
  }

  const [saveError, setSaveError] = useState<string | null>(null)

  const handleSubmit = async (data: {
    name: string
    rate: number
    description?: string
    category: string
    service?: string
    overtimeRate?: number
    publicWorksRate?: number
    unit?: string
    fuelCharge?: number
    variableCost?: boolean
    restripeCost?: number
    stripingCategory?: string
    newLayoutEnabled?: boolean
    newLayoutAdditionalCost?: number
    doubleCoatEnabled?: boolean
    doubleCoatAdditionalCost?: number
    serviceRates?: Record<string, { rate: number; overtimeRate: number; publicWorksRate: number }>
  }) => {
    setSaveError(null)

    // Build category-specific payload — only send fields relevant to this category
    const base = { name: data.name }
    let payload: Record<string, unknown> = { ...base }

    if (category === 'equipment') {
      payload = { ...payload, rate: data.rate, unit: data.unit || 'hr', service: data.service, fuelCharge: data.fuelCharge, serviceRates: data.serviceRates }
    } else if (category === 'labor') {
      payload = { ...payload, rate: data.rate ?? 0, unit: 'hr', service: data.service, serviceRates: data.serviceRates }
    } else if (category === 'materials') {
      payload = { ...payload, rate: data.rate, unit: data.unit || 'Ton', variableCost: data.variableCost, service: data.service, serviceRates: data.serviceRates }
    } else if (category === 'miscellaneous') {
      payload = { ...payload, rate: data.rate, unit: data.unit || 'each', service: data.service, serviceRates: data.serviceRates }
    } else if (category === 'striping') {
      payload = {
        ...payload,
        rate: data.restripeCost || 0,
        unit: data.unit || 'each',
        restripeCost: data.restripeCost,
        stripingCategory: data.stripingCategory,
        newLayoutEnabled: data.newLayoutEnabled,
        newLayoutAdditionalCost: data.newLayoutAdditionalCost,
        doubleCoatEnabled: data.doubleCoatEnabled,
        doubleCoatAdditionalCost: data.doubleCoatAdditionalCost,
      }
    }

    try {
      if (editingItem) {
        const updated = await pricingService.update(editingItem.id, payload)
        setItems(items.map((item) => (item.id === updated.id ? updated : item)))
      } else {
        const created = await pricingService.create({ ...payload, category } as any)
        setItems([...items, created])
      }
      setDialogOpen(false)
      setEditingItem(undefined)
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to save item'
      console.error('Failed to save item:', msg)
      setSaveError(msg)
    }
  }

  // Filter by service tab — equipment may have comma-separated services
  const tabItems = activeTab === 'All'
    ? items
    : items.filter((i) => {
        const val = i[serviceField] || ''
        return val.split(',').map(s => s.trim()).includes(activeTab)
      })

  return (
    <div>
      {/* Page Header — same pattern as estimates list */}
      <div className="est-list-head">
        <h1 className="est-list-title">{title}</h1>
        <button className="btn" onClick={handleAddClick}>+ Add {addLabel || title}</button>
      </div>

      {/* Search */}
      <div style={{ marginBottom: '16px' }}>
        <div className="search-wrap">
          <input
            type="text"
            className="search-input"
            placeholder="Search items..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <span className="search-icon">
            <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
          </span>
        </div>
      </div>

      {/* Service Tabs — not shown for miscellaneous */}
      {category !== 'miscellaneous' && (
        <div className="est-list-tabs">
          {tabs.map((tab) => (
            <button
              key={tab}
              className={`est-list-tab ${activeTab === tab ? 'active' : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>
      )}

      {/* Loading / Error states */}
      {loading ? (
        <div className="card est-list-card">
          <PageLoader />
        </div>
      ) : error ? (
        <div className="card est-list-card">
          <div style={{ padding: '48px', textAlign: 'center', color: 'var(--red)' }}>
            {error}
          </div>
        </div>
      ) : (
        /* Table in card */
        <div className="card est-list-card">
          <PricingItemsTable
            items={tabItems}
            category={category}
            onEdit={handleEdit}
            onDelete={handleDelete}
            searchQuery={search}
            onItemUpdated={(updated) => setItems(items.map(i => i.id === updated.id ? updated : i))}
          />
        </div>
      )}

      {/* Add/Edit Dialog */}
      <AddItemDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false)
          setEditingItem(undefined)
          setSaveError(null)
        }}
        onSubmit={handleSubmit}
        editItem={editingItem}
        category={category}
        error={saveError}
        defaultFuelChargePercent={defaultFuelChargePercent}
      />

      {/* Delete confirmation / refusal */}
      <DeleteItemDialog
        item={pendingDelete}
        blockedMessage={deleteBlocked}
        deleting={deleting}
        onConfirm={confirmDelete}
        onClose={closeDeleteDialog}
      />
    </div>
  )
}
